// Pilotage des abonnés (conception du 27/10/2026) : fonctions pures de l'écran du gestionnaire. Les coûts arrivent
// en dollars de la base ; ils s'affichent dans la monnaie du compte au cours de la BCE, en dollars sans cours.
import type { AccountCurrency } from "./accountCurrency";

// Types
export type SignalKind =
  | "low_balance"
  | "high_spend"
  | "inactive"
  | "pending_request"
  | "invited_long";
export type Signal = {
  kind: SignalKind;
  side: number;
  sales_id: number | null;
};
export type Person = {
  sales_id: number;
  first_name: string | null;
  last_name: string | null;
  email: string;
  partner: boolean;
  administrator: boolean;
  org_admin: boolean;
  disabled: boolean;
  partner_offer: string | null;
  invited_at: string | null;
  last_activity: string | null;
  credits_spent: number;
  cost_usd: number;
};
export type Side = {
  id: number;
  name: string | null;
  partner: boolean;
  partner_offer: string | null;
  members: number;
  balance: number;
  reserved: number;
  monthly_credits: number;
  credits_spent: number;
  cost_usd: number;
  last_activity: string | null;
  people: Person[];
};
export type Accounts = {
  month: string;
  usd_per_eur: number | null;
  spend_alert_eur: number;
  totals: {
    accounts_usd: number;
    unattributed_usd: number;
    common_usd: number;
    ai_spent_usd: number;
    ai_budget_usd: number | null;
    airscale_balance: number | null;
    airscale_read_at: string | null;
    active_accounts: number | null;
  };
  signals: Signal[];
  sides: Side[];
};

export const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

export const matchesSearch = (side: Side, query: string) => {
  const needle = normalize(query.trim());
  if (!needle) return true;
  const fields = [
    side.name ?? "",
    ...side.people.flatMap((p) => [
      p.first_name ?? "",
      p.last_name ?? "",
      p.email,
    ]),
  ];
  return fields.some((field) => normalize(field).includes(needle));
};

export const sortSides = (sides: Side[]) =>
  [...sides].sort(
    (a, b) =>
      b.cost_usd - a.cost_usd ||
      (a.name ?? "").localeCompare(b.name ?? "") ||
      a.id - b.id,
  );

export const costInCurrency = (
  usd: number,
  currency: AccountCurrency,
  usdPerEur: number | null,
): { value: number; currency: AccountCurrency } => {
  if (currency === "USD" || !usdPerEur) return { value: usd, currency: "USD" };
  return { value: usd / usdPerEur, currency: "EUR" };
};

const money = (
  value: number,
  currency: AccountCurrency,
  locale: string,
  maximumFractionDigits = 2,
) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 2,
    maximumFractionDigits,
  }).format(value);

/** Coût dans la monnaie du compte, au centime : les dépenses d'un compte sont de l'ordre de l'euro. */
export const formatCost = (
  usd: number,
  currency: AccountCurrency,
  usdPerEur: number | null,
  locale: string,
) => {
  const cost = costInCurrency(usd, currency, usdPerEur);
  return money(cost.value, cost.currency, locale);
};

/** Coût par crédit : souvent sous le centime, gardé au dix-millième pour rester lisible. */
export const formatUnitCost = (
  usd: number,
  currency: AccountCurrency,
  usdPerEur: number | null,
  locale: string,
) => {
  const cost = costInCurrency(usd, currency, usdPerEur);
  return money(
    cost.value,
    cost.currency,
    locale,
    Math.abs(cost.value) < 0.01 ? 4 : 2,
  );
};

/** Montant d'origine, au survol. */
export const formatUsd = (usd: number, locale: string) =>
  money(usd, "USD", locale);

export const costPerCredit = (usd: number, credits: number) =>
  credits > 0 ? usd / credits : null;

export const monthOptions = (now: Date, count = 6) =>
  Array.from({ length: count }, (_, i) => {
    const date = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1),
    );
    return date.toISOString().slice(0, 10);
  });
